import { authSplitApi } from 'redux/helpers/slice.helpers';
import { TAddPaymentMethod, TGetPaymentMethodParams, TInvoiceRecipient } from 'data/types/payment.types';
import {
  TPageableDataParamUI,
  TPageableDataWithContent,
  TPageableDataWithContentUi,
} from 'data/types/pagableData.type';
import { EPropertyMangerUrl } from 'data/urls/propertyManager.url';
import { queryParamConverter } from 'data/converters/general.converters';
import { pageableDataConverter } from 'data/converters/pagableData.converters';

const BASE_URL_PROPERTY_MANAGER = `${EPropertyMangerUrl.PROPERTY_MANAGER}`;

export const paymentApi = authSplitApi('paymentApi', [
  'paymentMethod',
  'paymentHistory',
  'invoiceRecipients',
]).injectEndpoints({
  endpoints: (build) => ({
    getPaymentMethod: build.query<any, { userType: string; params: TGetPaymentMethodParams }>({
      query: ({ userType, params }) => ({
        url: `${BASE_URL_PROPERTY_MANAGER}/payment`,
        method: 'GET',
        params,
      }),
      providesTags: ['paymentMethod'],
    }),
    addPaymentMethod: build.mutation<void, { userType: string; data: TAddPaymentMethod }>({
      query: ({ userType, data }) => ({
        url: `${BASE_URL_PROPERTY_MANAGER}/payment`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['paymentMethod'],
    }),

    getLandlordPaymentHistory: build.query<TPageableDataWithContentUi<any>, TPageableDataParamUI>({
      query({ search, pagination }) {
        return {
          url: `${BASE_URL_PROPERTY_MANAGER}/payment/transaction`,
          method: 'GET',
          params: queryParamConverter(search, pagination, {}, false, false),
        };
      },
      transformResponse: (data: TPageableDataWithContent<any>) => {
        const { content, ...other } = data;
        return {
          content: data.content,
          paginationData: pageableDataConverter.fromDb(other),
        };
      },
      providesTags: ['paymentHistory'],
    }),
    getUpcomingPaymentData: build.query<any, void>({
      query: () => ({
        url: `${BASE_URL_PROPERTY_MANAGER}/payment/upcoming`,
        method: 'GET',
      }),
      providesTags: ['paymentMethod'],
    }),
    getInvoiceRecipients: build.query<TInvoiceRecipient[], void>({
      query: () => ({
        url: `${BASE_URL_PROPERTY_MANAGER}/payment/invoice-recipients`,
        method: 'GET',
      }),
      providesTags: ['invoiceRecipients'],
    }),
    addInvoiceRecipients: build.mutation<TInvoiceRecipient[], string[]>({
      query: (emails) => ({
        url: `${BASE_URL_PROPERTY_MANAGER}/payment/invoice-recipients`,
        method: 'POST',
        body: { emails },
      }),
      invalidatesTags: ['invoiceRecipients'],
    }),
    deleteInvoiceRecipients: build.mutation<void, string[]>({
      query: (recipientIds) => ({
        url: `${BASE_URL_PROPERTY_MANAGER}/payment/invoice-recipients`,
        method: 'DELETE',
        body: { recipientIds },
      }),
      invalidatesTags: ['invoiceRecipients'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPaymentMethodQuery,
  useAddPaymentMethodMutation,
  useLazyGetPaymentMethodQuery,
  useLazyGetLandlordPaymentHistoryQuery,
  useLazyGetUpcomingPaymentDataQuery,
  useGetInvoiceRecipientsQuery,
  useAddInvoiceRecipientsMutation,
  useDeleteInvoiceRecipientsMutation,
} = paymentApi;
